import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { content } from "../i18n/content";
import { scrollToId } from "../lib/scroll";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const RotatingBadge = ({ text }) => (
  <div className="relative w-28 h-28 sm:w-36 sm:h-36 pointer-events-none" data-testid="hero-badge">
    <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow">
      <defs>
        <path id="badge-circle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
      </defs>
      <text className="fill-[#5c5656] uppercase" style={{ fontSize: 7.2, letterSpacing: "0.3em" }}>
        <textPath href="#badge-circle">{text}</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="w-2 h-2 rotate-45 bg-[#c86f53]" />
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const yImg = useTransform(scrollYProgress, [0, 1], ["-4%", "6%"]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen overflow-hidden flex items-center pt-32 pb-24"
      data-testid="hero-section"
    >
      <motion.div style={{ y: yBg }} className="absolute inset-0 pointer-events-none select-none" aria-hidden>
        <span className="font-display outline-text absolute -top-[4%] -left-[2%] text-[22vw] leading-none font-semibold italic">
          Lingua
        </span>
        <span className="absolute left-[6%] top-[20%] w-px h-48 bg-[#2a2626]/10" />
        <span className="absolute left-[6%] top-[20%] w-24 h-px bg-[#2a2626]/10" />
      </motion.div>

      <div className="relative z-10 max-w-[1500px] mx-auto w-full px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mb-8 sm:mb-10"
          >
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.32em] text-[#c86f53]" data-testid="hero-eyebrow">
              {content.hero.eyebrowDe}
            </p>
            <p className="mt-2 text-[11px] sm:text-xs uppercase tracking-[0.32em] text-[#6b705c]" data-testid="hero-eyebrow-en">
              {content.hero.eyebrowEn}
            </p>
          </motion.div>

          <h1
            className="font-display font-medium text-[#2a2626] text-[13.5vw] sm:text-[9.5vw] lg:text-[6.8vw] leading-[0.95] tracking-tight"
            data-testid="hero-heading"
          >
            <MaskedLine delay={0.35}>
              {content.hero.titleDe1} <span className="italic text-[#c86f53]">{content.hero.titleDe2}</span>
            </MaskedLine>
            <MaskedLine delay={0.55} className="italic font-light text-[#6b705c] text-[0.62em] mt-[0.3em]">
              {content.hero.titleEn}
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="mt-10 max-w-xl space-y-4"
            data-testid="hero-subtext"
          >
            <p className="text-sm sm:text-base leading-relaxed text-[#2a2626]/85 font-light">{content.hero.subDe}</p>
            <p className="font-display italic text-base sm:text-lg leading-relaxed text-[#6b705c] font-light">
              {content.hero.subEn}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-3 bg-[#2a2626] text-[#f9f6f0] rounded-full px-8 py-4 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c86f53] transition-colors duration-500"
              data-testid="hero-cta-primary"
            >
              {content.hero.ctaPrimary}
              <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollToId("services")}
              className="inline-flex items-center gap-3 border border-[#2a2626]/30 rounded-full px-8 py-4 text-[11px] uppercase tracking-[0.22em] text-[#2a2626] hover:border-[#2a2626] transition-colors duration-500"
              data-testid="hero-cta-secondary"
            >
              {content.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-3 lg:col-start-9 relative w-full max-w-xs sm:max-w-sm mx-auto lg:mx-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 translate-x-4 translate-y-4 border border-[#c86f53]/50 pointer-events-none"
            aria-hidden
          />
          <motion.figure
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-[#f2ebe1] p-3 border border-[#2a2626]/10"
          >
            <motion.img
              src="/portrait-uli.png"
              alt={content.hero.imageCaption}
              style={{ y: yImg }}
              className="w-full h-auto block"
              data-testid="hero-image"
            />
            <figcaption
              className="pt-3 pb-1 text-center text-[10px] uppercase tracking-[0.28em] text-[#5c5656]"
              data-testid="hero-image-caption"
            >
              {content.hero.imageCaption}
            </figcaption>
          </motion.figure>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -top-10 -left-8 sm:-left-12"
          >
            <RotatingBadge text={content.hero.badge} />
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 1 }}
        onClick={() => scrollToId("about")}
        className="absolute bottom-8 left-6 sm:left-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-[#5c5656] hover:text-[#2a2626] transition-colors duration-300"
        data-testid="hero-scroll-indicator"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={13} />
        </motion.span>
        {content.hero.scroll}
      </motion.button>
    </section>
  );
};
